/**
 * Static reference data for the case-detail "procedural timeline" tab.
 * Two parallel tracks — Israeli civil procedure (תקנות סדר הדין האזרחי
 * תשע"ט-2018) and criminal procedure (חוק סדר הדין הפלילי
 * [נוסח משולב] תשמ"ב-1982). Phase content is intentionally Hebrew-only:
 * these are statutory terms used by Israeli law firms; translating them
 * would obscure the source. The surrounding chrome (tab title, filters,
 * toggle) is i18n'd in cases.json.
 */

export type ProceduralPhaseKey =
  | 'submit'
  | 'prelim'
  | 'disc'
  | 'trial'
  | 'post'
  | 'invest'
  | 'charge'
  | 'verdict';

export type ProceduralPhase = {
  id: number;
  group: ProceduralPhaseKey;
  title: string;
  deadline: string;
  description: string;
  /** Tone hints — used to choose dot/pill colors. */
  bg: string;
  fg: string;
  border: string;
  deadlineBg: string;
  deadlineFg: string;
  tags: { label: string; bg: string; fg: string }[];
  details: string[];
  ref: string;
};

export type ProceduralFilter = { key: ProceduralPhaseKey | 'all'; label: string; color: string };

export const civilFilters: ProceduralFilter[] = [
  { key: 'all', label: 'הכל', color: '#1A4A6E' },
  { key: 'submit', label: 'הגשה', color: '#0F6E56' },
  { key: 'prelim', label: 'קדם משפט', color: '#854F0B' },
  { key: 'disc', label: 'גילוי', color: '#533AB7' },
  { key: 'trial', label: 'משפט', color: '#8B1A1A' },
  { key: 'post', label: 'לאחר פסיקה', color: '#5F5E5A' },
];

export const criminalFilters: ProceduralFilter[] = [
  { key: 'all', label: 'הכל', color: '#8B1A1A' },
  { key: 'invest', label: 'חקירה', color: '#7A4A00' },
  { key: 'charge', label: 'כתב אישום', color: '#533AB7' },
  { key: 'prelim', label: 'קדם משפט', color: '#854F0B' },
  { key: 'trial', label: 'שמיעת ראיות', color: '#8B1A1A' },
  { key: 'verdict', label: 'גזר דין', color: '#3A1A6E' },
  { key: 'post', label: 'לאחר פסיקה', color: '#5F5E5A' },
];

export const civilSteps: ProceduralPhase[] = [
  {
    id: 1,
    group: 'submit',
    title: 'הגשת כתב תביעה',
    deadline: 'יום 0',
    description: 'הגשת כתב התביעה לבית המשפט המוסמך בצירוף אגרה, תצהירים ומסמכים.',
    bg: '#E1F5EE',
    fg: '#085041',
    border: '#1D9E75',
    deadlineBg: '#E1F5EE',
    deadlineFg: '#085041',
    tags: [
      { label: 'תקנה 4', bg: '#E1F5EE', fg: '#085041' },
      { label: 'חובה', bg: '#FCEBEB', fg: '#791F1F' },
    ],
    details: [
      'פירוט העילה, הצדדים וסעד מבוקש',
      'תצהיר לאימות העובדות (תקנה 4)',
      'תשלום אגרת בית משפט',
      'מסמכים מהותיים שברשות התובע',
      'ייפוי כוח לעורך הדין',
    ],
    ref: 'תקנות 4–9 לתקנות סדר הדין האזרחי, תשע"ט-2018',
  },
  {
    id: 2,
    group: 'submit',
    title: 'המצאה לנתבע',
    deadline: 'עד 30 יום',
    description: 'המצאה אישית לנתבע לפי כללי ההמצאה הקבועים בתקנות.',
    bg: '#E1F5EE',
    fg: '#085041',
    border: '#1D9E75',
    deadlineBg: '#E1F5EE',
    deadlineFg: '#085041',
    tags: [{ label: 'תקנה 9', bg: '#E1F5EE', fg: '#085041' }],
    details: [
      'המצאה אישית עדיפה',
      'ניתן להמציא לבית מגורים או מורשה',
      'אישור המצאה יוגש לבית המשפט',
    ],
    ref: 'תקנות 9–20',
  },
  {
    id: 3,
    group: 'submit',
    title: 'כתב הגנה',
    deadline: '45 יום',
    description: 'הנתבע מגיש כתב הגנה מפורט כולל הכחשות וטענות מקדמיות.',
    bg: '#E1F5EE',
    fg: '#085041',
    border: '#1D9E75',
    deadlineBg: '#FAEEDA',
    deadlineFg: '#633806',
    tags: [
      { label: 'תקנה 10', bg: '#E1F5EE', fg: '#085041' },
      { label: '45 יום', bg: '#FAEEDA', fg: '#633806' },
    ],
    details: [
      'הכחשה מפורטת של כל עובדה שאינה מוסכמת',
      'טענות מקדמיות: חוסר סמכות, התיישנות',
      'תביעה שכנגד ניתן לצרף (תקנה 23)',
      'אי-הגשה = פסק דין בהיעדר הגנה',
    ],
    ref: 'תקנות 10–15',
  },
  {
    id: 4,
    group: 'submit',
    title: 'כתב תשובה',
    deadline: 'עד 15 יום',
    description: 'התובע רשאי להשיב לטענות חדשות שהועלו בכתב ההגנה.',
    bg: '#E1F5EE',
    fg: '#085041',
    border: '#1D9E75',
    deadlineBg: '#F1EFE8',
    deadlineFg: '#444441',
    tags: [
      { label: 'תקנה 16', bg: '#E1F5EE', fg: '#085041' },
      { label: 'רשות', bg: '#F1EFE8', fg: '#444441' },
    ],
    details: ['רשות, לא חובה', 'רק לטענות ההגנה החדשות'],
    ref: 'תקנה 16',
  },
  {
    id: 5,
    group: 'prelim',
    title: 'קדם משפט ראשון',
    deadline: '60–90 יום',
    description: 'שופט קדם המשפט דן בסוגיות מקדמיות ומגדיר גבולות המחלוקת.',
    bg: '#FEE9C4',
    fg: '#6A3A00',
    border: '#BA7517',
    deadlineBg: '#FAEEDA',
    deadlineFg: '#633806',
    tags: [
      { label: 'תקנה 60', bg: '#FAEEDA', fg: '#633806' },
      { label: 'חובה', bg: '#FCEBEB', fg: '#791F1F' },
    ],
    details: [
      'בירור הפלוגתאות השנויות במחלוקת',
      'הוראות לגילוי מסמכים',
      'בחינת אפשרות לגישור',
      'קביעת לוח זמנים',
    ],
    ref: 'תקנות 60–75',
  },
  {
    id: 6,
    group: 'disc',
    title: 'גילוי מסמכים הדדי',
    deadline: '30–45 יום',
    description: 'הצדדים מחליפים רשימות מסמכים וחושפים מסמכים רלוונטיים.',
    bg: '#EEEDFE',
    fg: '#3C3489',
    border: '#7F77DD',
    deadlineBg: '#EEEDFE',
    deadlineFg: '#3C3489',
    tags: [{ label: 'תקנה 46', bg: '#EEEDFE', fg: '#3C3489' }],
    details: [
      'רשימת מסמכים — כל מסמך רלוונטי, גם מחמיר',
      'גילוי יזום ללא בקשה מפורשת',
      'תצהיר אמת על שלמות הגילוי',
    ],
    ref: 'תקנות 46–58',
  },
  {
    id: 7,
    group: 'disc',
    title: 'שאלונים ועדויות',
    deadline: 'לפי הוראה',
    description: 'שאלונים כתובים ובקשות להודאות לפי הוראות בית המשפט.',
    bg: '#EEEDFE',
    fg: '#3C3489',
    border: '#7F77DD',
    deadlineBg: '#F1EFE8',
    deadlineFg: '#444441',
    tags: [{ label: 'תקנה 53', bg: '#EEEDFE', fg: '#3C3489' }],
    details: ['שאלון כתוב — עד 30 שאלות', 'תשובות מאומתות בתצהיר תוך 30 יום'],
    ref: 'תקנות 53–59',
  },
  {
    id: 8,
    group: 'prelim',
    title: 'קדם משפט שני',
    deadline: 'לפי הוראה',
    description: 'גיבוש פלוגתאות סופיות, הגשת ראיות, קביעת סדר שמיעה.',
    bg: '#FEE9C4',
    fg: '#6A3A00',
    border: '#BA7517',
    deadlineBg: '#FAEEDA',
    deadlineFg: '#633806',
    tags: [{ label: 'תקנה 67', bg: '#FAEEDA', fg: '#633806' }],
    details: [
      'אישור פלוגתאות סופיות',
      'קביעת רשימת עדים',
      'קביעת מועד ומשך שמיעת הראיות',
    ],
    ref: 'תקנות 67–71',
  },
  {
    id: 9,
    group: 'trial',
    title: 'שמיעת ראיות',
    deadline: 'לפי נוחות ביהמ"ש',
    description: 'חקירת עדים, הגשת ראיות, סיכומים בכתב.',
    bg: '#FAE8E8',
    fg: '#791F1F',
    border: '#E24B4A',
    deadlineBg: '#FCEBEB',
    deadlineFg: '#791F1F',
    tags: [
      { label: 'חקירת עדים', bg: '#FCEBEB', fg: '#791F1F' },
      { label: 'סיכומים', bg: '#FAEEDA', fg: '#633806' },
    ],
    details: [
      'עדויות ראשיות בכתב',
      'חקירה נגדית בעל-פה',
      'סיכומים בכתב תוך 45 יום',
    ],
    ref: 'תקנות 76–120',
  },
  {
    id: 10,
    group: 'trial',
    title: 'פסק דין',
    deadline: '90 יום מסיום',
    description: 'בית המשפט מחויב לתת פסק דין בתוך 90 יום מסיום הדיון.',
    bg: '#FAE8E8',
    fg: '#791F1F',
    border: '#E24B4A',
    deadlineBg: '#FCEBEB',
    deadlineFg: '#791F1F',
    tags: [
      { label: 'סעיף 74 לחוק', bg: '#FCEBEB', fg: '#791F1F' },
      { label: '90 יום', bg: '#FAEEDA', fg: '#633806' },
    ],
    details: [
      'פסק דין מנומק בכתב',
      'ניתן לפסוק הוצאות משפט',
      'בקשת הבהרה תוך 30 יום',
    ],
    ref: 'סעיף 74 לחוק בתי המשפט',
  },
  {
    id: 11,
    group: 'post',
    title: 'ערעור',
    deadline: '45 יום מפסה"ד',
    description: 'ערעור לבית המשפט הגבוה יותר בנושאי עובדה ומשפט.',
    bg: '#D3D1C7',
    fg: '#444441',
    border: '#888780',
    deadlineBg: '#F1EFE8',
    deadlineFg: '#444441',
    tags: [
      { label: 'תקנה 127', bg: '#F1EFE8', fg: '#444441' },
      { label: '45 יום', bg: '#FAEEDA', fg: '#633806' },
    ],
    details: ['ערעור בזכות מהשלום למחוזי', 'פיקדון ערעור — בקשת עיכוב ביצוע'],
    ref: 'תקנות 127–145',
  },
];

export const criminalSteps: ProceduralPhase[] = [
  {
    id: 1,
    group: 'invest',
    title: 'חקירת משטרה',
    deadline: 'ללא הגבלה חוקית',
    description:
      'המשטרה חוקרת את החשד לביצוע עבירה, כולל חקירת חשודים, עדים ואיסוף ראיות.',
    bg: '#FEE9C4',
    fg: '#6A3A00',
    border: '#BA7517',
    deadlineBg: '#FAEEDA',
    deadlineFg: '#633806',
    tags: [
      { label: 'חוק סד"פ', bg: '#FAEEDA', fg: '#633806' },
      { label: 'חקירה', bg: '#FEE9C4', fg: '#6A3A00' },
    ],
    details: [
      'חקירת חשוד — זכות להיות מיוצג ע"י עו"ד',
      'אזהרה לחשוד לפני חקירה (סעיף 28)',
      'חיפוש ותפיסה — בצו שיפוטי ברוב המקרים',
      'מעצר ראשוני עד 24 שעות ללא צו שיפוטי',
      'הגשת בקשה להארכת מעצר עד 15 יום',
    ],
    ref: 'סעיפים 2–59 לחוק סדר הדין הפלילי [נוסח משולב], תשמ"ב-1982',
  },
  {
    id: 2,
    group: 'invest',
    title: 'מעצר ימים',
    deadline: 'עד 30 יום (ניתן להארכה)',
    description:
      'בית משפט מחוזי רשאי להאריך מעצר לצרכי חקירה בהתאם לתנאים שנקבעו בחוק.',
    bg: '#FEE9C4',
    fg: '#6A3A00',
    border: '#BA7517',
    deadlineBg: '#FAEEDA',
    deadlineFg: '#633806',
    tags: [
      { label: 'סעיף 62', bg: '#FAEEDA', fg: '#633806' },
      { label: 'ביהמ"ש מחוזי', bg: '#E6F1FB', fg: '#0C447C' },
    ],
    details: [
      'שמיעה בפני שופט מחוזי תוך 48 שעות ממעצר',
      'זכות ייצוג משפטי בדיון המעצר',
      'מעצר עד תום ההליכים — לעבירות חמורות',
      'ניתן לשחרר בערובה ובתנאים מגבילים',
      'ביהמ"ש שוקל: חומרת העבירה, ראיות, סיכון',
    ],
    ref: 'סעיפים 21–62 — מעצר',
  },
  {
    id: 3,
    group: 'charge',
    title: 'הגשת כתב אישום',
    deadline: 'לפי שיקול דעת התביעה',
    description:
      'פרקליטות המדינה או המשטרה (בעבירות קלות) מגישות כתב אישום לבית המשפט.',
    bg: '#EEEDFE',
    fg: '#3C3489',
    border: '#7F77DD',
    deadlineBg: '#EEEDFE',
    deadlineFg: '#3C3489',
    tags: [
      { label: 'סעיף 85', bg: '#EEEDFE', fg: '#3C3489' },
      { label: 'תביעה', bg: '#F1EFE8', fg: '#444441' },
    ],
    details: [
      'כתב אישום — פירוט העובדות והסעיפים הפליליים',
      'הגשה לבית משפט בעל סמכות (שלום/מחוזי)',
      'ניתן להגיש כתב אישום מתוקן עם אישור ביהמ"ש',
      'כתב אישום מגדיר את גבולות ההליך הפלילי',
      'הנאשם מקבל העתק כתב האישום',
    ],
    ref: 'סעיפים 85–100 — כתב האישום',
  },
  {
    id: 4,
    group: 'charge',
    title: 'הקראה ורישום מענה',
    deadline: 'בהקדם האפשרי',
    description:
      'ביהמ"ש מקריא את כתב האישום לנאשם ורושם את מענהו — הודאה או כפירה.',
    bg: '#EEEDFE',
    fg: '#3C3489',
    border: '#7F77DD',
    deadlineBg: '#EEEDFE',
    deadlineFg: '#3C3489',
    tags: [
      { label: 'סעיף 153', bg: '#EEEDFE', fg: '#3C3489' },
      { label: 'הודאה/כפירה', bg: '#F1EFE8', fg: '#444441' },
    ],
    details: [
      'הקראת כתב האישום בפני הנאשם',
      'הנאשם רשאי להודות בחלק מהאישומים',
      'הודאה בעובדות — עשויה לקצר את ההליך',
      'כפירה — ביהמ"ש קובע מועד להוכחות',
      'ניתן לנהל הסדר טיעון בשלב זה',
    ],
    ref: 'סעיפים 153–157 — הקראה ומענה',
  },
  {
    id: 5,
    group: 'prelim',
    title: 'קדם משפט פלילי',
    deadline: 'לפי הוראת ביהמ"ש',
    description:
      'שלב קדם-משפטי לבירור טענות מקדמיות, הסדרי ראיות ואפשרות לסיים ההליך.',
    bg: '#FAE8E8',
    fg: '#791F1F',
    border: '#E24B4A',
    deadlineBg: '#FCEBEB',
    deadlineFg: '#791F1F',
    tags: [
      { label: 'סעיף 143', bg: '#FCEBEB', fg: '#791F1F' },
      { label: 'חובה', bg: '#FCEBEB', fg: '#791F1F' },
    ],
    details: [
      'טענות מקדמיות: חסינות, מעשה בית דין, אי-כשירות',
      'בקשות לפסילת ראיות שהושגו שלא כדין',
      'הסדר טיעון — סיכום פסקה של אחריות ועונש',
      'גילוי חומר חקירה לסנגור (חובה)',
      'ביהמ"ש עשוי לדחות אישומים בשלב זה',
    ],
    ref: 'סעיפים 143–152 — קדם משפט',
  },
  {
    id: 6,
    group: 'prelim',
    title: 'גילוי חומר חקירה',
    deadline: 'לפני תחילת ההוכחות',
    description:
      'התביעה מחויבת לחשוף לסנגור את כל חומר החקירה, כולל ראיות לזכות.',
    bg: '#FAE8E8',
    fg: '#791F1F',
    border: '#E24B4A',
    deadlineBg: '#FCEBEB',
    deadlineFg: '#791F1F',
    tags: [
      { label: 'סעיף 74', bg: '#FCEBEB', fg: '#791F1F' },
      { label: 'חובה', bg: '#FCEBEB', fg: '#791F1F' },
    ],
    details: [
      'גילוי מלא של חומר חקירה כולל מה שלזכות',
      'חומר חסוי — ניתן לעיין בפני שופט בלבד',
      'זכות לקבל העתקים של ראיות מהותיות',
      'חוות דעת מומחים של התביעה',
      'ניתן לבקש השלמת גילוי בבית המשפט',
    ],
    ref: 'סעיף 74 לחוק סדר הדין הפלילי',
  },
  {
    id: 7,
    group: 'trial',
    title: 'שמיעת ראיות — תביעה',
    deadline: 'לפי קביעת ביהמ"ש',
    description:
      'התביעה מציגה ראיותיה: עדים, מסמכים, חוות דעת מומחים וראיות פורנזיות.',
    bg: '#791F1F',
    fg: '#FCEBEB',
    border: '#E24B4A',
    deadlineBg: '#FCEBEB',
    deadlineFg: '#791F1F',
    tags: [
      { label: 'עדי תביעה', bg: '#FCEBEB', fg: '#791F1F' },
      { label: 'חקירה נגדית', bg: '#FAEEDA', fg: '#633806' },
    ],
    details: [
      'עד מדינה — נאשם שהפך עד תביעה',
      'עדות ראשית — בדרך כלל בעל-פה (בניגוד לאזרחי)',
      'חקירה נגדית על ידי הסנגור',
      'עדי מומחים: רופאים, מעבדה, פסיכיאטרים',
      'ביהמ"ש רשאי לשאול עדים ישירות',
    ],
    ref: 'סעיפים 158–162 — שמיעת עדים',
  },
  {
    id: 8,
    group: 'trial',
    title: 'שמיעת ראיות — הגנה',
    deadline: 'לאחר סיום התביעה',
    description:
      'ההגנה מציגה ראיותיה ועדיה, כולל הנאשם עצמו אם בחר להעיד.',
    bg: '#791F1F',
    fg: '#FCEBEB',
    border: '#E24B4A',
    deadlineBg: '#FCEBEB',
    deadlineFg: '#791F1F',
    tags: [
      { label: 'עדי הגנה', bg: '#FCEBEB', fg: '#791F1F' },
      { label: 'זכות שתיקה', bg: '#F1EFE8', fg: '#444441' },
    ],
    details: [
      'הנאשם רשאי לשתוק — שתיקה לא ניתנת לפרשנות לרעה',
      'הנאשם שבחר להעיד — נחקר כעד רגיל',
      'עדי אופי — לתמוך בשם הטוב של הנאשם',
      'בקשת זיכוי בסיום ראיות התביעה (סעיף 158א)',
      'עדי מומחים מטעם ההגנה',
    ],
    ref: 'סעיפים 158א–166 — ראיות ההגנה',
  },
  {
    id: 9,
    group: 'verdict',
    title: 'סיכומים ופסק דין',
    deadline: 'בהקדם האפשרי',
    description:
      'הצדדים מגישים סיכומים בכתב או בעל-פה, ולאחריהם ביהמ"ש נותן פסק דין.',
    bg: '#3A1A6E',
    fg: '#EEE8FA',
    border: '#7F77DD',
    deadlineBg: '#EEEDFE',
    deadlineFg: '#3C3489',
    tags: [
      { label: 'סיכומים', bg: '#EEEDFE', fg: '#3C3489' },
      { label: 'הרשעה/זיכוי', bg: '#FCEBEB', fg: '#791F1F' },
    ],
    details: [
      'סיכומי התביעה — עיקרי הראיות והעמדה המשפטית',
      'סיכומי ההגנה — ספקות סבירים, טיעוני עקרון',
      'ביהמ"ש קובע אשמה מעל לספק סביר',
      'הרשעה — קובעת אחריות בלבד, לא עונש',
      'זיכוי — מסיים ההליך הפלילי',
    ],
    ref: 'סעיפים 182–184 — פסק הדין',
  },
  {
    id: 10,
    group: 'verdict',
    title: 'שלב הטיעונים לעונש',
    deadline: 'לאחר הרשעה בלבד',
    description:
      'לאחר הרשעה, שני הצדדים מציגים טיעונים לעניין חומרת העונש.',
    bg: '#3A1A6E',
    fg: '#EEE8FA',
    border: '#7F77DD',
    deadlineBg: '#EEEDFE',
    deadlineFg: '#3C3489',
    tags: [
      { label: 'סעיף 185', bg: '#EEEDFE', fg: '#3C3489' },
      { label: 'לאחר הרשעה', bg: '#FCEBEB', fg: '#791F1F' },
    ],
    details: [
      'דוח קצין מבחן — בדיקת רקע הנאשם',
      'תסקיר שירות מבחן — גורמי סיכון ומסוגלות שיקום',
      'טיעוני תביעה: חומרת עבירה, נסיבות, קורבן',
      'טיעוני הגנה: נסיבות מקילות, עבר פלילי',
      'הצהרת קורבן עבירה (סעיף 197א)',
    ],
    ref: 'סעיפים 185–195 — גזר הדין',
  },
  {
    id: 11,
    group: 'verdict',
    title: 'גזר דין',
    deadline: 'לאחר טיעוני עונש',
    description:
      'ביהמ"ש גוזר את עונשו של הנאשם — מאסר, קנס, עבודות שירות או שחרור על תנאי.',
    bg: '#3A1A6E',
    fg: '#EEE8FA',
    border: '#7F77DD',
    deadlineBg: '#EEEDFE',
    deadlineFg: '#3C3489',
    tags: [
      { label: 'סעיף 190', bg: '#EEEDFE', fg: '#3C3489' },
      { label: 'עונש', bg: '#FCEBEB', fg: '#791F1F' },
    ],
    details: [
      'עונשים אפשריים: מאסר בפועל, על תנאי, קנס',
      'עבודות שירות לציבור — עד 9 חודשים',
      'שחרור בפיקוח לשם שיקום',
      'צו פיצויים לקורבן העבירה',
      'הנאשם רשאי לפנות לוועדת שחרורים לאחר ⅓ עונש',
    ],
    ref: 'סעיפים 190–213 — גזר הדין וסוגי העונשים',
  },
  {
    id: 12,
    group: 'post',
    title: 'ערעור פלילי',
    deadline: '45 יום מגזה"ד',
    description:
      'הנאשם והתביעה רשאים לערער על פסק הדין ועל חומרת העונש.',
    bg: '#D3D1C7',
    fg: '#444441',
    border: '#888780',
    deadlineBg: '#F1EFE8',
    deadlineFg: '#444441',
    tags: [
      { label: 'סעיף 210', bg: '#F1EFE8', fg: '#444441' },
      { label: '45 יום', bg: '#FAEEDA', fg: '#633806' },
    ],
    details: [
      'ערעור על הרשעה ועל חומרת העונש',
      'גם התביעה רשאית לערער על קולת העונש',
      'ביהמ"ש עליון — בית דין אחרון בסוגיות עקרוניות',
      'ניתן לבקש עיכוב ביצוע עונש מאסר',
      'בקשה לעיון חוזר בפסק דין שגוי',
    ],
    ref: 'סעיפים 210–220 — ערעורים פליליים',
  },
];
